import axios from 'axios';
import { getToken, getRefreshToken, setToken, setRefreshToken, clearTokens } from './auth';
// Seed from a persisted impersonation so the very first request after a reload
// (before React mounts) already carries the right tenant header. Kept in sync
// afterwards by CommuneProvider. Key mirrors ImpersonationContext's STORAGE_KEY.
let communeSlug = (() => {
    try {
        const raw = sessionStorage.getItem('impersonated_commune');
        return raw ? (JSON.parse(raw).slug ?? null) : null;
    }
    catch {
        return null;
    }
})();
export function setCommuneSlug(slug) {
    communeSlug = slug;
}
/** Commune envoyée dans X-Commune-Slug (pour les appels hors axios, comme le flux de l'assistant). */
export function getCommuneSlug() {
    return communeSlug;
}
export const API_BASE_URL = 'https://api.macommunelive.fr';
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
});
api.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    if (communeSlug) {
        config.headers['X-Commune-Slug'] = communeSlug;
    }
    return config;
});
let isRefreshing = false;
let failedQueue = [];
function processQueue(error, token) {
    failedQueue.forEach(({ resolve, reject }) => {
        if (token)
            resolve(token);
        else
            reject(error);
    });
    failedQueue = [];
}
api.interceptors.response.use((response) => response, async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url ?? '';
    // Don't intercept auth endpoints — let them fail naturally
    if (url.includes('/auth/login') || url.includes('/auth/me') || url.includes('/auth/register')) {
        return Promise.reject(error);
    }
    // Espace bloqué pendant la session : retour à la connexion avec le motif.
    const blockCode = error.response?.status === 403 ? error.response.data?.code : null;
    if (blockCode === 'AGENT_BLOCKED' || blockCode === 'PRO_BLOCKED') {
        clearTokens();
        window.location.href = `/login?blocked=${blockCode}`;
        return Promise.reject(error);
    }
    if (error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
    }
    if (isRefreshing) {
        return new Promise((resolve, reject) => {
            failedQueue.push({
                resolve: (token) => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    resolve(api(originalRequest));
                },
                reject,
            });
        });
    }
    originalRequest._retry = true;
    isRefreshing = true;
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
    }
    try {
        const { data } = await axios.post('https://api.macommunelive.fr/api/v1/auth/refresh', {
            refresh_token: refreshToken,
        });
        setToken(data.token);
        setRefreshToken(data.refresh_token);
        processQueue(null, data.token);
        originalRequest.headers.Authorization = `Bearer ${data.token}`;
        return api(originalRequest);
    }
    catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
    }
    finally {
        isRefreshing = false;
    }
});
export default api;
